import { addDatadogRumAction, getDatadogClientStatus } from '@/utils/tracing';

/**
 * Records the *shape* of an unhandled promise rejection — never its content.
 *
 * Some rejections reach RUM stripped of everything that identifies them: no
 * stack frames, and a `message` identical to the `name`. Nothing in the error
 * event says what kind of value was actually rejected, and session replay
 * cannot help because it records DOM mutations, not rejected values. The shape
 * fields below settle that in a single sample — a plain object literal carries
 * its error fields as own keys while a real `DOMException` keeps them on the
 * prototype, and a value that fails `instanceof` while still naming its
 * constructor likely crossed a realm boundary (e.g. a same-origin iframe) or
 * is an impostor class that never extended `Error`.
 *
 * Deliberately observation-only: no `preventDefault()` and no
 * `stopImmediatePropagation()`, so RUM files the error exactly as before.
 */

/** Distinct shapes reported per page load before further ones are ignored. */
const MAX_REPORTED_SHAPES = 20;

const MAX_PROPERTY_NAMES = 20;

const MAX_NAME_LENGTH = 60;

/**
 * Structural error keys safe to report verbatim. Anything else — which could
 * be a dynamically computed, data-bearing key — reports as the placeholder,
 * preserving key count and position without the name itself.
 */
const SAFE_PROPERTY_NAMES = new Set([
  'name',
  'message',
  'stack',
  'cause',
  'code',
  'status',
  'statusText',
  'type',
  'detail',
  'reason',
  'errors',
  'config',
  'request',
  'response',
  'isAxiosError',
  'fileName',
  'lineNumber',
  'columnNumber',
  'sourceURL',
  'line',
  'column',
]);

const REDACTED_PROPERTY_NAME = '[redacted]';

const reportedShapes = new Set<string>();

const truncate = (value: string): string => value.slice(0, MAX_NAME_LENGTH);

// V8 frames are `    at fn (url:line:col)`; JavaScriptCore and SpiderMonkey
// frames are `fn@url:line:col`. Count both so a genuine Safari stack is not
// mistaken for a header-only one.
const isStackFrame = (line: string): boolean =>
  /^\s*at\s/.test(line) || /@.*:\d+/.test(line);

export type RejectionShape = {
  value_type: string;
  constructor_name: string;
  error_name: string;
  is_error: boolean;
  is_dom_exception: boolean;
  has_stack: boolean;
  /** 0 when a stack string exists but carries no frames — the giveaway. */
  stack_frame_count: number;
  has_cause: boolean;
  own_property_names: string[];
  /** True when any property read threw — a proxy/hostile value, not an empty one. */
  read_failed: boolean;
  visibility_state: string;
};

/**
 * Describes a rejected value using type and key information only. Beyond type
 * checks, the only strings that leave this function are the `name`, the
 * constructor name, and an allowlisted set of structural property keys — no
 * message, no values, no free-form keys.
 */
export const describeRejectionShape = (reason: unknown): RejectionShape => {
  let readFailed = false;
  /** Reads a possibly-throwing getter without letting it escape. */
  const read = <T>(get: () => T): T | undefined => {
    try {
      return get();
    } catch {
      readFailed = true;
      return undefined;
    }
  };

  const asRecord = reason as Record<string, unknown> | null | undefined;
  const stack = read(() => asRecord?.stack);
  const hasStack = typeof stack === 'string' && stack.length > 0;
  const name = read(() => asRecord?.name);
  const constructorName = read(
    () => (reason as { constructor?: { name?: unknown } })?.constructor?.name
  );
  const isObjectLike =
    reason !== null && (typeof reason === 'object' || typeof reason === 'function');
  const ownPropertyNames = isObjectLike
    ? (read(() => Object.getOwnPropertyNames(reason)) ?? [])
        .slice(0, MAX_PROPERTY_NAMES)
        .map((key) => (SAFE_PROPERTY_NAMES.has(key) ? key : REDACTED_PROPERTY_NAME))
    : [];

  return {
    value_type: typeof reason,
    constructor_name:
      typeof constructorName === 'string' ? truncate(constructorName) : '',
    error_name: typeof name === 'string' ? truncate(name) : '',
    is_error: read(() => reason instanceof Error) ?? false,
    is_dom_exception:
      read(() => typeof DOMException !== 'undefined' && reason instanceof DOMException) ??
      false,
    has_stack: hasStack,
    stack_frame_count: hasStack ? stack.split('\n').filter(isStackFrame).length : 0,
    has_cause: read(() => asRecord?.cause !== undefined) ?? false,
    own_property_names: ownPropertyNames,
    read_failed: readFailed,
    visibility_state:
      typeof document === 'undefined' ? 'unknown' : document.visibilityState,
  };
};

/**
 * Groups rejections that repeat the same shape. A loop re-throwing one value
 * matches on every field, so only its first occurrence reports — while the
 * discriminating booleans keep e.g. a cross-realm `DOMException` distinct from
 * a same-realm one that happens to share its name.
 */
const buildShapeSignature = (shape: RejectionShape): string =>
  [
    shape.value_type,
    shape.constructor_name,
    shape.error_name,
    shape.is_error,
    shape.is_dom_exception,
    shape.has_stack,
  ].join('|');

/**
 * Reports the first occurrence of each distinct rejection shape.
 *
 * Skipped entirely while RUM is uninitialised so a boot-time rejection cannot
 * consume a shape's single report before there is anywhere to send it — the
 * next occurrence reports instead. Wrapped so a failure here can never escape
 * the handler.
 */
export const handleRejectionShapeProbe = (event: PromiseRejectionEvent): void => {
  try {
    if (!getDatadogClientStatus().rumInitialized) return;
    if (reportedShapes.size >= MAX_REPORTED_SHAPES) return;

    const shape = describeRejectionShape(event.reason);
    const signature = buildShapeSignature(shape);
    if (reportedShapes.has(signature)) return;

    // Marked reported only after the sink accepts it, so a sink failure lets
    // the shape's next occurrence retry instead of losing it for the session.
    addDatadogRumAction('unhandled_rejection_shape', shape);
    reportedShapes.add(signature);
  } catch {
    // A diagnostic must never destabilise the page it observes.
  }
};

/**
 * Installs {@link handleRejectionShapeProbe} on `window`. Web and desktop both
 * install it — the rejection this exists to identify has been seen on Chrome
 * and Safari alike.
 *
 * This probe suppresses nothing, so it never needs to run before another
 * listener. Listeners installed ahead of it that stop propagation (the offline
 * network guard) hide those rejections from the probe — acceptable, as they
 * are already identified. Returns a cleanup that removes the listener.
 */
export const installRejectionShapeProbe = (): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;
  window.addEventListener('unhandledrejection', handleRejectionShapeProbe);
  return () =>
    window.removeEventListener('unhandledrejection', handleRejectionShapeProbe);
};

/** Test-only: clears the reported-shape set between cases. */
export const __resetRejectionShapeProbeForTests = (): void => {
  reportedShapes.clear();
};
